import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mic, FileText, Shield, CreditCard, Users, ArrowRight, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import LoginModal from './LoginModal';

const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowLogin(true);
    }
  };

  const handleLoginClose = () => {
    setShowLogin(false);
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    navigate('/dashboard');
  };

  // Features section data
  const features = [
    {
      icon: <Mic className="w-10 h-10 text-teal-500" aria-label="Advanced Voice Recognition" />,
      title: 'Advanced Voice Recognition',
      description: 'Our state-of-the-art voice recognition technology delivers industry-leading accuracy for all your transcription needs.'
    },
    {
      icon: <FileText className="w-10 h-10 text-teal-500" aria-label="Quick PDF Delivery" />,
      title: 'Quick PDF Delivery',
      description: 'Receive beautifully formatted PDF transcripts delivered directly to your dashboard, typically within minutes.'
    },
    {
      icon: <Shield className="w-10 h-10 text-teal-500" aria-label="Secure & Confidential" />,
      title: 'Secure & Confidential',
      description: 'All your audio files and transcripts are processed with enterprise-grade security and strict confidentiality.'
    },
    {
      icon: <CreditCard className="w-10 h-10 text-teal-500" aria-label="Simple Pay-As-You-Go" />,
      title: 'Simple Pay-As-You-Go',
      description: 'No monthly fees or subscriptions. Only pay for what you use with our transparent credit system.'
    },
    {
      icon: <Users className="w-10 h-10 text-teal-500" aria-label="Multi-Language Support" />,
      title: 'Multi-Language Support',
      description: 'Transcribe content in multiple languages with the same high level of accuracy and quality.'
    },
    {
      icon: <ArrowRight className="w-10 h-10 text-teal-500" aria-label="Easy Integration" />,
      title: 'Easy Integration',
      description: 'Download your transcripts and easily integrate them with your existing workflow and tools.'
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      quote: "This transcription service has revolutionized our content production workflow. The speed and accuracy are unmatched.",
      author: "Sarah Johnson",
      position: "Content Director, MediaHub"
    },
    {
      quote: "I've tried many transcription services, but this one consistently delivers the best results for my research interviews.",
      author: "Dr. Michael Chen",
      position: "Research Professor, University of Technology"
    },
    {
      quote: "The dashboard is intuitive and the transcription quality is exceptional. It's now an essential part of our podcast production.",
      author: "Emma Rodriguez",
      position: "Podcast Producer, AudioWave Studios"
    }
  ];

  // Pricing tiers
  const pricingInfo = {
    standard: {
      rate: "£1.50",
      unit: "per minute",
      minimum: "£5.00 minimum per file"
    }
  };

  return (
    <>
      <Helmet>
        <title>Voice2Text - Professional Audio Transcription Service | UK Based</title>
        <meta name="description" content="Convert audio to text with 99% accuracy. Our AI-powered transcription service delivers fast results for podcasts, interviews, lectures, and meetings. UK-based with global support." />
        <meta name="keywords" content="voice to text, audio transcription, transcription service, speech recognition, podcast transcription, interview transcription, meeting transcription, lecture transcription, UK transcription service, AI transcription" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Voice2Text - Professional Audio Transcription Service | UK Based" />
        <meta property="og:description" content="Convert audio to text with 99% accuracy. Fast, secure transcription for podcasts, interviews, and meetings." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voice2text.com" />
        <meta property="og:image" content="https://voice2text.com/og-image.jpg" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:site_name" content="Voice2Text" />
        
        {/* Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Voice2Text - Professional Audio Transcription Service" />
        <meta name="twitter:description" content="Convert audio to text with 99% accuracy. Our AI-powered transcription service delivers fast results." />
        <meta name="twitter:image" content="https://voice2text.com/twitter-image.jpg" />
        
        <link rel="canonical" href="https://voice2text.com" />
        <meta name="robots" content="index, follow" />
        
        {/* Structured Data for Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Voice2Text Audio Transcription",
            "description": "Professional audio transcription service with industry-leading accuracy and security.",
            "provider": {
              "@type": "Organization",
              "name": "Voice2Text",
              "url": "https://voice2text.com"
            },
            "offers": {
              "@type": "Offer",
              "price": "1.50",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "1.50",
                "priceCurrency": "GBP",
                "unitCode": "MIN",
                "unitText": "per minute"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <header className="relative bg-gradient-to-r from-teal-500 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Mic className="h-8 w-8 mr-2" aria-label="Voice2Text Logo" />
              <span className="text-2xl font-bold">Voice2Text</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-white hover:text-teal-200 transition">Features</a>
              <a href="#how-it-works" className="text-white hover:text-teal-200 transition">How It Works</a>
              <a href="#pricing" className="text-white hover:text-teal-200 transition">Pricing</a>
              <a href="#testimonials" className="text-white hover:text-teal-200 transition">Testimonials</a>
            </nav>
            <button 
              onClick={handleLoginClick}
              className="bg-white text-teal-600 px-5 py-2 rounded-md font-semibold hover:bg-teal-50 transition flex items-center"
            >
              <LogIn className="h-5 w-5 mr-1" />
              {isAuthenticated ? 'Dashboard' : 'Login'}
            </button>
          </div>

          <div className="py-16 md:py-24 flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-8 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                Turn Speech Into Text With Incredible Accuracy
              </h1>
              <p className="text-xl mb-8 max-w-md">
                Our advanced AI-powered transcription service delivers fast, accurate text from your audio files. Perfect for podcasts, interviews, lectures, and meetings.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <button 
                  onClick={handleLoginClick}
                  className="bg-white text-teal-700 font-semibold py-3 px-6 rounded-md hover:bg-teal-50 transition"
                >
                  Get Started Now
                </button>
                <a 
                  href="#how-it-works"
                  className="bg-transparent border-2 border-white text-white font-semibold py-3 px-6 rounded-md hover:bg-white hover:bg-opacity-10 transition text-center"
                >
                  Learn More
                </a>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-white rounded-lg shadow-xl p-6 transform rotate-2 relative">
                <div className="absolute -top-2 -left-2 bg-teal-500 text-white text-xs px-2 py-1 rounded">
                  Audio File
                </div>
                <div className="h-12 bg-gray-100 rounded-md mb-4 flex items-center px-4">
                  <Mic className="h-5 w-5 text-teal-500 mr-2" aria-label="Audio file icon" />
                  <span className="text-gray-500">interview_session_045.mp3</span>
                </div>
                <div className="h-48 bg-gray-50 rounded-md overflow-y-auto">
                  <div className="p-4 text-gray-700 text-sm font-mono">
                    <p><strong>Speaker 1:</strong> Thank you for joining us today to discuss the future of remote work.</p>
                    <p><strong>Speaker 2:</strong> It's great to be here. I believe remote work is fundamentally changing how companies operate.</p>
                    <p><strong>Speaker 1:</strong> What do you see as the biggest challenge for distributed teams?</p>
                    <p><strong>Speaker 2:</strong> Communication and maintaining company culture are certainly challenging, but the benefits of accessing global talent are tremendous.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" fill="#ffffff">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
          </svg>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Industry-Leading Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our transcription service offers everything you need to convert your audio to text quickly and accurately.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="p-6 border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition bg-white">
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our streamlined process makes it easy to get high-quality transcriptions in just a few simple steps.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center md:space-x-8">
            <div className="md:w-1/3 mb-8 md:mb-0">
              <div className="bg-white p-6 rounded-lg shadow-md relative">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center text-xl font-bold absolute -top-4 -left-4">1</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-3">Upload Your Audio</h3>
                <p className="text-gray-600">Simply upload your audio file from your dashboard. We support all common audio formats.</p>
              </div>
            </div>
            
            <div className="w-12 h-2 md:w-2 md:h-24 bg-teal-500 rounded-full mb-8 md:mb-0"></div>
            
            <div className="md:w-1/3 mb-8 md:mb-0">
              <div className="bg-white p-6 rounded-lg shadow-md relative">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center text-xl font-bold absolute -top-4 -left-4">2</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-3">We Process Your File</h3>
                <p className="text-gray-600">Our advanced AI transcribes your audio with industry-leading accuracy, typically within minutes.</p>
              </div>
            </div>
            
            <div className="w-12 h-2 md:w-2 md:h-24 bg-teal-500 rounded-full mb-8 md:mb-0"></div>
            
            <div className="md:w-1/3">
              <div className="bg-white p-6 rounded-lg shadow-md relative">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center text-xl font-bold absolute -top-4 -left-4">3</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-3">Download Your Text</h3>
                <p className="text-gray-600">Receive a notification when your transcript is ready, and download it directly from your dashboard.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              No subscriptions or hidden fees. Only pay for what you use with our credit-based system.
            </p>
          </div>
          
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
            <div className="md:flex">
              <div className="md:shrink-0 bg-teal-500 md:w-48 flex items-center justify-center p-6">
                <div className="text-center text-white">
                  <span className="text-4xl font-bold">{pricingInfo.standard.rate}</span>
                  <span className="block">{pricingInfo.standard.unit}</span>
                </div>
              </div>
              <div className="p-8">
                <div className="text-xl font-semibold text-gray-900">Standard Transcription</div>
                <p className="mt-2 text-gray-600">{pricingInfo.standard.minimum}</p>
                <ul className="mt-4 text-gray-600">
                  <li className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-teal-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    High-quality transcription
                  </li>
                  <li className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-teal-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Multi-speaker detection
                  </li>
                  <li className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-teal-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Formatted PDF delivery
                  </li>
                  <li className="flex items-center">
                    <svg className="h-5 w-5 text-teal-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Fast turnaround time
                  </li>
                </ul>
                <button 
                  onClick={handleLoginClick}
                  className="mt-6 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-4 rounded transition"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hear from professionals who rely on our transcription services every day.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-teal-500 mb-4">
                  {/* Quote icon */}
                  <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-gray-500 text-sm">{testimonial.position}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-teal-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who trust our service for their transcription needs.
          </p>
          <button 
            onClick={handleLoginClick}
            className="bg-white text-teal-700 font-semibold py-3 px-8 rounded-md hover:bg-teal-50 transition"
          >
            Start Transcribing Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between">
            <div className="mb-8 md:mb-0">
              <div className="flex items-center mb-4">
                <Mic className="h-6 w-6 mr-2 text-teal-400" />
                <span className="text-xl font-bold text-white">Voice2Text</span>
              </div>
              <p className="max-w-xs text-gray-400">
                Professional audio transcription service with industry-leading accuracy and security.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-white">Company</h3>
                <ul className="space-y-2">
                  <li><a href="/about" className="hover:text-teal-400 transition">About Us</a></li>
                  <li><a href="/careers" className="hover:text-teal-400 transition">Careers</a></li>
                  <li><a href="/privacy-policy" className="hover:text-teal-400 transition">Privacy Policy</a></li>
                  <li><a href="/terms-of-service" className="hover:text-teal-400 transition">Terms of Service</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4 text-white">Resources</h3>
                <ul className="space-y-2">
                  <li><a href="/documentation" className="hover:text-teal-400 transition">Documentation</a></li>
                  <li><a href="/api" className="hover:text-teal-400 transition">API</a></li>
                  <li><a href="/blog" className="hover:text-teal-400 transition">Blog</a></li>
                  <li><a href="/support" className="hover:text-teal-400 transition">Support Center</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4 text-white">Contact</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <svg className="h-5 w-5 mr-2 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    transcriptions@voice2text.work
                  </li>
                  <li className="flex items-center">
                    <svg className="h-5 w-5 mr-2 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    +44 020 1234 5678
                  </li>
                </ul>
                <div className="mt-4 flex space-x-4">
                  <a href="#" className="text-gray-400 hover:text-white transition">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.963-.479-1.38-.899-.42-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.48-.963.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.54 0c1.356 0 2.46 1.104 2.46 2.472v19.056c0 1.368-1.104 2.472-2.46 2.472H4.48c-1.368 0-2.48-1.104-2.48-2.472V2.472C2 1.104 3.112 0 4.48 0h15.06zm-7.8 16.392c-1.728 0-3.12-1.392-3.12-3.12 0-1.728 1.392-3.12 3.12-3.12 1.728 0 3.12 1.392 3.12 3.12 0 1.728-1.392 3.12-3.12 3.12zm9.12-9.12H3.12V5.04h17.76v2.232z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-800 pt-8 text-sm text-gray-400">
            <p> 2024 Voice2Text. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-auto relative animate-fadeIn"
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button 
                onClick={handleLoginClose}
                className="bg-white rounded-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-4">
                  <Mic className="h-10 w-10 text-teal-600 mr-2" />
                  <h2 className="text-3xl font-bold text-gray-900">Voice2Text</h2>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Welcome Back</h3>
                <p className="text-gray-600 mt-1">Sign in to your account to continue</p>
              </div>
              {/* Login form content is now handled by the LoginModal component */}
              <button
                onClick={handleLoginClick}
                className="w-full bg-teal-600 text-white py-2.5 px-4 rounded-md font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors flex items-center justify-center"
              >
                <LogIn className="h-5 w-5 mr-2" />
                Sign in to your account
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLogin} 
        onClose={handleLoginClose} 
        onSuccess={handleLoginSuccess} 
      />
    </>
  );
};

export default LandingPage;